import { z } from 'zod';

/**
 * Resource types, as `<service>:<resource-type>`. With an account and an id they form a
 * Trinity Resource Name (TRN): `trn:<service>:<account-id>:<resource-type>/<id>`.
 * Global resource types leave the account segment empty: `trn:<service>::<resource-type>/<id>`.
 */
export enum ResourceTypeEnum {
  Console = 'mgmt:console',
  Account = 'mgmt:account',
  Agent = 'mgmt:agent',
  Application = 'mgmt:application',
  AuditLog = 'mgmt:audit-log',
  AuthApp = 'mgmt:auth-app',
  Brand = 'mgmt:brand',
  ChatContext = 'mgmt:chat-context',
  ConnectedApp = 'mgmt:connected-app',
  CrmConnection = 'mgmt:crm-connection',
  Dashboard = 'mgmt:dashboard',
  DataConnection = 'mgmt:data-connection',
  DataSource = 'mgmt:data-source',
  Filter = 'mgmt:filter',
  Form = 'mgmt:form',
  Policy = 'mgmt:policy',
  Prompt = 'mgmt:prompt',
  PromptQueryLog = 'mgmt:prompt-query-log',
  Role = 'mgmt:role',
  Secret = 'mgmt:secret',
  Tool = 'mgmt:tool',
  UnmetNeed = 'mgmt:unmet-need',
  User = 'mgmt:user',

  ChatTrace = 'chat:trace',
  Hcp = 'chat:hcp',

  QueueDashboard = 'ops:queue-dashboard',
}
export const ResourceType = z.enum(ResourceTypeEnum);
export type ResourceType = z.infer<typeof ResourceType>;

/** User-facing names for resource types, for UIs, reports and error messages. */
export const RESOURCE_TYPE_NAMES: Record<ResourceTypeEnum, string> = {
  [ResourceTypeEnum.Console]: 'Management Console',
  [ResourceTypeEnum.Account]: 'Account',
  [ResourceTypeEnum.Agent]: 'Agent',
  [ResourceTypeEnum.Application]: 'Application',
  [ResourceTypeEnum.AuditLog]: 'Audit Log',
  [ResourceTypeEnum.AuthApp]: 'Authentication App',
  [ResourceTypeEnum.Brand]: 'Brand',
  [ResourceTypeEnum.ChatContext]: 'Chat Context',
  [ResourceTypeEnum.ConnectedApp]: 'Connected App',
  [ResourceTypeEnum.CrmConnection]: 'CRM Connection',
  [ResourceTypeEnum.Dashboard]: 'Dashboard',
  [ResourceTypeEnum.DataConnection]: 'Data Connection',
  [ResourceTypeEnum.DataSource]: 'Data Source',
  [ResourceTypeEnum.Filter]: 'Filter',
  [ResourceTypeEnum.Form]: 'Form',
  [ResourceTypeEnum.Policy]: 'Policy',
  [ResourceTypeEnum.Prompt]: 'Prompt',
  [ResourceTypeEnum.PromptQueryLog]: 'Prompt Query Log',
  [ResourceTypeEnum.Role]: 'Role',
  [ResourceTypeEnum.Secret]: 'Secret',
  [ResourceTypeEnum.Tool]: 'Tool',
  [ResourceTypeEnum.UnmetNeed]: 'Unmet Need',
  [ResourceTypeEnum.User]: 'User',
  [ResourceTypeEnum.ChatTrace]: 'Chat Trace',
  [ResourceTypeEnum.Hcp]: 'HCP',
  [ResourceTypeEnum.QueueDashboard]: 'Queue Dashboard',
};

export enum ResourceScopeEnum {
  /** Owned by one account (a row in `groups`), directly or through a brand. */
  Account = 'account',
  /** Platform-wide; shared by every account. */
  Global = 'global',
}

/**
 * Which resource types belong to an account. Only shapes the TRN today; policies don't yet
 * restrict by account. Global types may still have per-brand settings (e.g. agent or
 * application features), but the resource itself is shared.
 */
export const RESOURCE_TYPE_SCOPE: Record<ResourceTypeEnum, ResourceScopeEnum> = {
  [ResourceTypeEnum.Console]: ResourceScopeEnum.Global,
  // The account itself: its TRN carries its own identifier.
  [ResourceTypeEnum.Account]: ResourceScopeEnum.Account,
  [ResourceTypeEnum.Agent]: ResourceScopeEnum.Global,
  [ResourceTypeEnum.Application]: ResourceScopeEnum.Global,
  // audit.entries.group_id
  [ResourceTypeEnum.AuditLog]: ResourceScopeEnum.Account,
  [ResourceTypeEnum.AuthApp]: ResourceScopeEnum.Global,
  // brands.group_id / groups_to_brands
  [ResourceTypeEnum.Brand]: ResourceScopeEnum.Account,
  [ResourceTypeEnum.ChatContext]: ResourceScopeEnum.Global,
  [ResourceTypeEnum.ConnectedApp]: ResourceScopeEnum.Global,
  [ResourceTypeEnum.CrmConnection]: ResourceScopeEnum.Global,
  [ResourceTypeEnum.Dashboard]: ResourceScopeEnum.Global,
  [ResourceTypeEnum.DataConnection]: ResourceScopeEnum.Global,
  [ResourceTypeEnum.DataSource]: ResourceScopeEnum.Global,
  [ResourceTypeEnum.Filter]: ResourceScopeEnum.Global,
  // form_schemas.brand_id (form workflows share this type and are gated with brand view)
  [ResourceTypeEnum.Form]: ResourceScopeEnum.Account,
  [ResourceTypeEnum.Policy]: ResourceScopeEnum.Global,
  [ResourceTypeEnum.Prompt]: ResourceScopeEnum.Global,
  [ResourceTypeEnum.PromptQueryLog]: ResourceScopeEnum.Global,
  [ResourceTypeEnum.Role]: ResourceScopeEnum.Global,
  [ResourceTypeEnum.Secret]: ResourceScopeEnum.Global,
  [ResourceTypeEnum.Tool]: ResourceScopeEnum.Global,
  // unmet_needs.brand_id
  [ResourceTypeEnum.UnmetNeed]: ResourceScopeEnum.Account,
  // users_to_groups
  [ResourceTypeEnum.User]: ResourceScopeEnum.Account,

  // chat_instances.brand_identifier: a chat (and its traces) belongs to a brand's account
  [ResourceTypeEnum.ChatTrace]: ResourceScopeEnum.Account,
  // HCP data is visible per account/brand
  [ResourceTypeEnum.Hcp]: ResourceScopeEnum.Account,

  [ResourceTypeEnum.QueueDashboard]: ResourceScopeEnum.Global,
};

/** The services whose resources policies cover; the first segment of a resource type and of an action. */
export enum ServiceEnum {
  Management = 'mgmt',
  Chat = 'chat',
  Operations = 'ops',
}
export const Service = z.enum(ServiceEnum);

/** User-facing names for services, for UIs and reports. */
export const SERVICE_NAMES: Record<ServiceEnum, string> = {
  [ServiceEnum.Management]: 'Management App',
  [ServiceEnum.Chat]: 'Chat',
  [ServiceEnum.Operations]: 'Operations',
};

/** User-facing names for resource scopes, for UIs and reports. */
export const RESOURCE_SCOPE_NAMES: Record<ResourceScopeEnum, string> = {
  [ResourceScopeEnum.Account]: 'Account',
  [ResourceScopeEnum.Global]: 'Global',
};

/** The service a resource type belongs to. */
export function getResourceTypeService(resourceType: ResourceTypeEnum): ServiceEnum {
  return Service.parse(resourceType.split(':')[0]);
}

export const TRN_PREFIX = 'trn';

/** Matches every resource. The only resource value policies accept for now. */
export const ANY_RESOURCE = '*';

/** Matches every account in an account-scoped TRN. */
export const ANY_ACCOUNT = '*';

/** Canonical uuid: lowercase, hyphenated. TRNs only ever contain this form. */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/** Hyphen-less uuid, as several tables store their `identifier` (agents, tools, chat contexts, ...). */
const COMPACT_UUID_PATTERN = /^[0-9a-f]{32}$/;

/**
 * A resource's uuid `identifier` (never its auto-increment primary key), in canonical form.
 * Accepts the hyphen-less form some tables store, so equal resources always yield equal TRNs.
 * Returns null for anything that isn't a uuid.
 */
export function toResourceId(identifier: string): string | null {
  const value = identifier.toLowerCase();
  if (UUID_PATTERN.test(value)) return value;
  if (COMPACT_UUID_PATTERN.test(value)) {
    return `${value.slice(0, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}-${value.slice(16, 20)}-${value.slice(20)}`;
  }
  return null;
}

/** Account-scoped types need an account (uuid or `*`); global types must have an empty account segment. */
function isValidAccountSegment(resourceType: ResourceTypeEnum, accountId: string): boolean {
  if (RESOURCE_TYPE_SCOPE[resourceType] === ResourceScopeEnum.Global) return accountId === '';
  return accountId === ANY_ACCOUNT || UUID_PATTERN.test(accountId);
}

export type ParsedResourceName = {
  resourceType: ResourceTypeEnum;
  /** Account (group) identifier or `*` for account-scoped types; null for global types. */
  accountId: string | null;
  /** The resource's uuid `identifier`, canonical form. */
  id: string;
};

export type BuildResourceNameParams =
  | { resourceType: ResourceTypeEnum; accountId: string; id: string }
  | { resourceType: ResourceTypeEnum; accountId?: undefined; id: string };

/**
 * Builds a TRN. `id` is the resource's uuid `identifier`, not its primary key. Pass
 * `accountId` (group identifier, or `*`) for account-scoped types and omit it for global
 * ones; see {@link RESOURCE_TYPE_SCOPE}.
 */
export function buildResourceName(params: BuildResourceNameParams): string {
  const id = toResourceId(params.id);
  const accountId = params.accountId?.toLowerCase() ?? '';
  if (!isValidAccountSegment(params.resourceType, accountId)) {
    const scope = RESOURCE_TYPE_SCOPE[params.resourceType];
    throw new Error(`Invalid account for ${scope} resource type ${params.resourceType}: '${accountId}'`);
  }
  if (!id) {
    throw new Error(`Invalid resource id (expected the resource's uuid identifier): ${params.id}`);
  }

  const [service, type] = params.resourceType.split(':');
  return `${TRN_PREFIX}:${service}:${accountId}:${type}/${id}`;
}

// Obviously synthetic, so an example can never be mistaken for a real resource.
const EXAMPLE_ACCOUNT_ID = '00000000-0000-7000-8000-00000000acc1';
const EXAMPLE_RESOURCE_ID = '00000000-0000-7000-8000-0000000000e1';

/** A TRN for the resource type with placeholder ids, to show what one looks like. */
export function buildExampleResourceName(resourceType: ResourceTypeEnum): string {
  return RESOURCE_TYPE_SCOPE[resourceType] === ResourceScopeEnum.Account
    ? buildResourceName({ resourceType, accountId: EXAMPLE_ACCOUNT_ID, id: EXAMPLE_RESOURCE_ID })
    : buildResourceName({ resourceType, id: EXAMPLE_RESOURCE_ID });
}

export function parseResourceName(value: string): ParsedResourceName | null {
  const slash = value.indexOf('/');
  if (slash === -1) return null;

  const segments = value.slice(0, slash).split(':');
  if (segments.length !== 4) return null;

  const [prefix, service, accountId, type] = segments;
  const resourceType = ResourceType.safeParse(`${service}:${type}`);
  const id = value.slice(slash + 1);
  // Only the canonical uuid form is a valid TRN id.
  if (prefix !== TRN_PREFIX || !resourceType.success || !UUID_PATTERN.test(id)) return null;
  if (!isValidAccountSegment(resourceType.data, accountId)) return null;

  return { resourceType: resourceType.data, accountId: accountId === '' ? null : accountId, id };
}

export const ResourceNameSchema = z.string().refine((value) => parseResourceName(value) !== null, {
  message:
    'Expected a resource name of the form trn:<service>:<account-id>:<resource-type>/<uuid> (empty account for global types)',
});
